import React, { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Loader2, Lock, X } from 'lucide-react';
import { getToken } from '../../services/tokenService';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const Filigrane = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
    <div className="absolute -inset-1/2 flex flex-wrap content-start gap-x-16 gap-y-24 rotate-[-30deg] opacity-[0.16]">
      {Array.from({ length: 60 }).map((_, i) => (
        <span key={i} className="whitespace-nowrap text-2xl font-black tracking-widest text-red-700">EN COURS DE VALIDATION</span>
      ))}
    </div>
  </div>
);

// Aperçu en lecture seule : pas de téléchargement, d'impression ni de copie ; flouté si la fenêtre perd le focus.
export const VisionneuseProtegee = ({ url, titre, onClose }) => {
  const [fichier, setFichier] = useState(null);
  const [pages, setPages] = useState(0);
  const [cache, setCache] = useState(false);
  const [erreur, setErreur] = useState('');
  const largeur = useRef(Math.min(window.innerWidth - 64, 860));

  useEffect(() => {
    let annule = false;
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((b) => { if (!annule) setFichier({ data: new Uint8Array(b) }); })
      .catch((e) => !annule && setErreur(e.message));
    return () => { annule = true; };
  }, [url]);

  useEffect(() => {
    const masquer = () => setCache(true);
    const montrer = () => setCache(false);
    const touches = (e) => {
      const k = (e.key || '').toLowerCase();
      if (k === 'printscreen' || ((e.ctrlKey || e.metaKey) && ['p', 's', 'c', 'a'].includes(k))) {
        e.preventDefault();
        setCache(true);
        navigator.clipboard?.writeText?.('').catch(() => {});
      }
    };
    const visib = () => (document.hidden ? masquer() : montrer());
    window.addEventListener('blur', masquer);
    window.addEventListener('focus', montrer);
    window.addEventListener('keydown', touches, true);
    window.addEventListener('keyup', touches, true);
    document.addEventListener('visibilitychange', visib);
    return () => {
      window.removeEventListener('blur', masquer);
      window.removeEventListener('focus', montrer);
      window.removeEventListener('keydown', touches, true);
      window.removeEventListener('keyup', touches, true);
      document.removeEventListener('visibilitychange', visib);
    };
  }, []);

  return (
    <div className="fixed inset-0 z-[200] flex flex-col bg-slate-950/90 backdrop-blur-sm print:hidden" data-testid="visionneuse-protegee"
      onContextMenu={(e) => e.preventDefault()} onDragStart={(e) => e.preventDefault()} style={{ userSelect: 'none', WebkitUserSelect: 'none' }}>
      <style>{'@media print { body * { display: none !important; } }'}</style>
      <div className="flex items-center gap-3 border-b border-white/10 bg-[#0a3d62] px-5 py-3 text-white">
        <Lock className="h-4 w-4 text-amber-300" />
        <p className="flex-1 truncate text-sm font-semibold">{titre}</p>
        <span className="rounded-full bg-amber-400 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#0a3d62]" data-testid="visionneuse-statut">En cours de validation</span>
        <button type="button" onClick={onClose} className="rounded p-1.5 hover:bg-white/10" data-testid="visionneuse-fermer"><X className="h-5 w-5" /></button>
      </div>
      <p className="bg-amber-50 px-5 py-2 text-center text-xs text-amber-900">
        Consultation uniquement : téléchargement et impression disponibles dès la validation du dossier par COPPAM Garanties Solidaires.
      </p>
      <div className={`flex-1 overflow-auto py-6 transition-[filter] duration-150 ${cache ? 'blur-2xl' : ''}`}>
        {erreur && <p className="text-center text-sm text-red-300" data-testid="visionneuse-erreur">Document indisponible ({erreur})</p>}
        {!fichier && !erreur && <div className="flex justify-center py-20 text-white"><Loader2 className="h-8 w-8 animate-spin" /></div>}
        {fichier && (
          <Document file={fichier} onLoadSuccess={({ numPages }) => setPages(numPages)} loading={null}>
            {Array.from({ length: pages }).map((_, i) => (
              <div key={i} className="relative mx-auto mb-4 w-fit shadow-2xl" data-testid={`visionneuse-page-${i + 1}`}>
                <Page pageNumber={i + 1} width={largeur.current} renderTextLayer={false} renderAnnotationLayer={false} />
                <Filigrane />
              </div>
            ))}
          </Document>
        )}
      </div>
    </div>
  );
};
